import type { ComponentProps } from 'react';
import {
  TooltipProps,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  LineChart,
} from 'recharts';
import { ChartTooltipWrapper } from '@/components/charts/ChartTooltip';

// Override grid styles so the grid lines are dashed
const CHART_GRID_CLASSNAME =
  '[&_.recharts-cartesian-grid-vertical>line]:[stroke-dasharray:0] [&_.recharts-cartesian-grid-horizontal>line:first-of-type]:[stroke-dasharray:0]';

export interface TimeSeriesLineChartProps<T extends object> {
  /** Chart data (array of points with a date key and value keys). */
  data: T[];
  /** Key in each data point for the x-axis (e.g. "date", "calculation_date"). */
  dateKey: string;
  xAxisTickFormatter: (tick: string, index: number) => string;
  yAxisFormatter: (value: number) => string;
  /** Accessible label for the chart. */
  ariaLabel: string;
  /** Renders tooltip content. Receives the payload (data point) when active. */
  tooltipContent: (payload: T) => React.ReactNode;
  /** Line and other chart elements (e.g. Recharts Line components). */
  children: React.ReactNode;
  /** Additional XAxis props to override defaults (e.g. `type: "number"` for non-date axes). */
  xAxisProps?: Partial<ComponentProps<typeof XAxis>>;
  /** Fixed height for the container. */
  height?: number;
  minHeight?: number;
  maxHeight?: number;
}

/**
 * Shared shell for time-series line charts: ResponsiveContainer, LineChart, XAxis,
 * YAxis, Tooltip wrapper, and CartesianGrid. Callers supply data, formatters,
 * tooltip content, and Line children.
 */
function TimeSeriesLineChart<T extends object>({
  data,
  dateKey,
  xAxisTickFormatter,
  yAxisFormatter,
  ariaLabel,
  tooltipContent,
  children,
  xAxisProps,
  height,
  minHeight,
  maxHeight,
}: TimeSeriesLineChartProps<T>): React.ReactElement {
  const containerProps =
    height != null
      ? { height, width: '100%' as const }
      : { minHeight: minHeight ?? 400, maxHeight: maxHeight ?? 700, width: '100%' as const };

  return (
    <ResponsiveContainer {...containerProps}>
      <LineChart
        data={data}
        role="img"
        aria-label={ariaLabel}
        margin={{ top: 16 }}
        className={CHART_GRID_CLASSNAME}
      >
        <XAxis
          stroke="#A1A4AA"
          dataKey={dateKey}
          tickLine={false}
          tickFormatter={xAxisTickFormatter}
          axisLine={true}
          tick={{ fill: '#8B8B8B', fontSize: '0.75rem' }}
          interval="preserveStart"
          minTickGap={16}
          {...xAxisProps}
        />
        <YAxis
          tickLine={false}
          axisLine={true}
          stroke="#646464"
          orientation="left"
          tick={({ x, y, payload }) => (
            <g transform={`translate(${x},${y})`}>
              <text y={5} textAnchor="end" fill="#646464" fontSize="0.75rem">
                {yAxisFormatter(Number(payload.value))}
              </text>
            </g>
          )}
        />
        <Tooltip
          isAnimationActive={true}
          animationDuration={300}
          filterNull={false}
          content={({ active, payload }: TooltipProps<number, string>) => {
            const datum = payload?.[0]?.payload as T | undefined;
            return (
              <ChartTooltipWrapper>
                {active && datum ? tooltipContent(datum) : null}
              </ChartTooltipWrapper>
            );
          }}
        />
        <CartesianGrid
          strokeDasharray="4 4"
          strokeWidth="1"
          stroke="#F0F0F9"
          verticalCoordinatesGenerator={(props) => [props.width * 0.333, props.width * 0.666]}
        />
        {children}
      </LineChart>
    </ResponsiveContainer>
  );
}

export default TimeSeriesLineChart;
